/* eslint-disable no-underscore-dangle */

// Based on Meteor's EJSON implementation: https://github.com/meteor/meteor
//
//    packages/oauth/oauth_client.js
//    2018-01-18
//
// The MIT License (MIT)
//
// Copyright (c) 2011 - 2017 Meteor Development Group, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import bind from 'lodash/bind';
import has from 'lodash/has';
import includes from 'lodash/includes';
import debug from './debug';
import Reload from './Reload';
import stateParam from './stateParam';
import { showPopup } from './OAuthBrowser';

// credentialToken -> credentialSecret. You must provide both the
// credentialToken and the credentialSecret to retrieve an access token from
// the _pendingCredentials collection.
const credentialSecrets = {};

const OAuth = {
  _storageTokenPrefix: 'Meteor.oauth.credentialSecret-',
};

OAuth.showPopup = showPopup;

// Determine the login style (popup or redirect) for this login flow.
OAuth._loginStyle = function _loginStyle(service, config, options) {
  let loginStyle =
    (options && options.loginStyle) || config.loginStyle || 'popup';

  if (!includes(['popup', 'redirect'], loginStyle)) {
    throw new Error(`Invalid login style: ${loginStyle}`);
  }

  // If we don't have session storage (for example, Safari in private
  // mode), the redirect login flow won't work, so fallback to the
  // popup style.
  if (loginStyle === 'redirect') {
    try {
      sessionStorage.setItem('Meteor.oauth.test', 'test');
      sessionStorage.removeItem('Meteor.oauth.test');
    } catch (e) {
      loginStyle = 'popup';
    }
  }

  return loginStyle;
};

OAuth._stateParam = stateParam;

// At the beginning of the redirect login flow, before we redirect to
// the login service, save the credential token for this login attempt
// in the reload migration data.
//
OAuth.saveDataForRedirect = function saveDataForRedirect(
  loginService,
  credentialToken,
) {
  Reload._onMigrate('oauth', () => {
    return [
      true,
      {
        loginService,
        credentialToken,
      },
    ];
  });
  Reload._migrate(null, {
    immediateMigration: true,
  });
};

// At the end of the redirect login flow, when we've redirected back
// to the application, retrieve the credentialToken and (if the login
// was successful) the credentialSecret.
//
// Called at application startup.  Returns null if this is normal
// application startup and we weren't just redirected at the end of
// the login flow.
//
OAuth.getDataAfterRedirect = function getDataAfterRedirect() {
  const migrationData = Reload._migrationData('oauth');

  if (!(migrationData && migrationData.credentialToken)) {
    return null;
  }

  const { credentialToken } = migrationData;
  const key = OAuth._storageTokenPrefix + credentialToken;
  let credentialSecret;
  try {
    credentialSecret = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
  } catch (e) {
    debug('error retrieving credentialSecret', e);
  }
  return {
    credentialToken,
    credentialSecret,
    loginService: migrationData.loginService,
  };
};

// Launch an OAuth login flow.  For the popup login style, show the
// popup.  For the redirect login style, save the credential token for
// this login attempt in the reload migration data, and redirect to
// the service for the login.
//
// options:
//  loginService: "facebook", "google", etc.
//  loginStyle: "popup" or "redirect"
//  loginUrl: The URL at the login service provider to start the OAuth flow.
//  credentialRequestCompleteCallback: for the popup flow, call when the popup
//    is closed and we have the credential from the login service.
//  credentialToken: our identifier for this login flow.
//
OAuth.launchLogin = function launchLogin(options) {
  if (!options.loginService) {
    throw new Error('loginService required');
  }
  if (options.loginStyle === 'popup') {
    OAuth.showPopup(
      options.loginUrl,
      bind(
        options.credentialRequestCompleteCallback,
        null,
        options.credentialToken,
      ),
      options.popupOptions,
    );
  } else if (options.loginStyle === 'redirect') {
    OAuth.saveDataForRedirect(options.loginService, options.credentialToken);
    window.location = options.loginUrl;
  } else {
    throw new Error('invalid login style');
  }
};

// Called by the popup when the OAuth flow is completed, right before
// the popup closes.
OAuth._handleCredentialSecret = function _handleCredentialSecret(
  credentialToken,
  secret,
) {
  // check(credentialToken, String);
  // check(secret, String);
  if (!has(credentialSecrets, credentialToken)) {
    credentialSecrets[credentialToken] = secret;
  } else {
    throw new Error('Duplicate credential token from OAuth login');
  }
};

// Used by accounts-oauth, which needs both a credentialToken and the
// corresponding to credential secret to call the `login` method over DDP.
OAuth._retrieveCredentialSecret = function _retrieveCredentialSecret(
  credentialToken,
) {
  // First check the secrets collected by OAuth._handleCredentialSecret,
  // then check localStorage. This matches what we do in
  // end_of_login_response.html.
  let secret = credentialSecrets[credentialToken];
  if (!secret) {
    const localStorageKey = OAuth._storageTokenPrefix + credentialToken;
    secret = localStorage.getItem(localStorageKey);
    localStorage.removeItem(localStorageKey);
  } else {
    delete credentialSecrets[credentialToken];
  }
  return secret;
};

export default OAuth;
